import * as React from 'react';
import { AuthService } from './auth/AuthService';
import { User } from 'oidc-client';
import { AuthenticatedApp } from './AuthenticatedApp'

export enum IUserType {
    CareProvider = 1,
    Municipality = 2
}

interface AppContentState {
    user?: User | null;
    loading?: boolean;
    userRoleGroup?: string;
    isAuthenticated?: boolean;
    userType?: IUserType;
}

export interface AuthContextModel {
    isAuthenticated: boolean;
    user?: User | null;
    userRolesGroup?: string;
    login: () => any;
    logout: () => any;
    setUserRolesGroup: (userRoleGroupId: string, userType: IUserType) => any;
    userType: IUserType;
}

const InitialState: AuthContextModel = {
    isAuthenticated: false,
    logout: () => { },
    login: () => { },
    setUserRolesGroup: (userRoleGroupId: string, userType: IUserType) => { },
    userType: IUserType.CareProvider
}

const LocalStorageKey = "por.ss";

const AuthContext = React.createContext<AuthContextModel>(InitialState);
export const AuthConsumer = AuthContext.Consumer;
export const useAuth = () => React.useContext(AuthContext);

export class App extends React.Component<{}, AppContentState> {
    public authService: AuthService;
    private shouldCancel: boolean;

    constructor(props: any) {
        super(props);

        var storedState = localStorage.getItem(LocalStorageKey);

        if (storedState !== null) {
            var parsedStoredState = JSON.parse(storedState);
            parsedStoredState.loading = true;
            this.state = parsedStoredState;
        }
        else {
            this.state = { loading: true };
        }

        this.authService = new AuthService();
        this.shouldCancel = false;
        this.login = this.login.bind(this);
        this.logout = this.logout.bind(this);
        this.setUserRolesGroup = this.setUserRolesGroup.bind(this);
    }

    public componentDidMount() {
        this.getUser();
        this.authService.userManager.events.addAccessTokenExpired(this.accessTokenExpired);
        this.authService.userManager.events.addAccessTokenExpiring(this.signinSilent);
    }

    public componentWillUnmount() {
        this.shouldCancel = true;
        this.authService.userManager.events.removeAccessTokenExpired(() => { });
    }

    private accessTokenExpired = () => {
        this.setState({ isAuthenticated: false });
    }

    private signinSilent = () => {
        this.authService.userManager.signinSilent()
            .then((user) => {
                this.getUser();
            })
            .catch((err) => {
                console.log(err);
                this.setState({ isAuthenticated: false });
            });
    };

    private storeState() {
        var storedState: AppContentState = {
            userRoleGroup: this.state.userRoleGroup,
            userType: this.state.userType
        };
        localStorage.setItem(LocalStorageKey, JSON.stringify(storedState));
    }

    private clearState() {
        localStorage.removeItem(LocalStorageKey);
    }

    public login() {
        this.authService.login();
    };

    public logout() {
        this.clearState();
        this.authService.logout();
    };

    public getUser() {
        this.authService.getUser().then((user: User | null) => {
            if (user) {
                console.log('User has been successfully loaded.');
                this.setState({ isAuthenticated: true });
            } else {
                console.log('User not found.');
            }

            if (!this.shouldCancel) {
                this.setState({ loading: false, user: user });
            }
        });
    };

    public setUserRolesGroup(rolesGroup: string, userType: IUserType) {
        this.setState({ userRoleGroup: rolesGroup, userType: userType }, () => this.storeState());
    }

    private renderApp() {
        var app: any;

        if (this.state.user !== null) {
            app = <AuthenticatedApp />;
        }
        else {
            this.login();
        }

        return (
            <AuthContext.Provider value={{
                isAuthenticated: this.state.isAuthenticated ?? false,
                user: this.state.user,
                userRolesGroup: this.state.userRoleGroup,
                login: this.login,
                logout: this.logout,
                setUserRolesGroup: this.setUserRolesGroup,
                userType: this.state.userType ?? IUserType.CareProvider
            }}>
                {app}
            </AuthContext.Provider>
        );
    }

    public render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderApp();

        return (
            <div>
                {contents}
            </div>
        );
    }
}
